import { toast } from "react-toastify";
import { HttpStatusCode, isAxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { READ_ONLY_TOOLTIP_MESSAGE } from "@/utils/permissions";

// 登記取得専用画面（propertyCodeAcquisition）の API エラー共通ハンドラ。
//
// 取得実行 hook・リユース確認 hook が共有する catch 分岐（403/409/400/5xx/汎用トースト）。
// 非 Axios 例外を握りつぶさず Sentry 送信し、400 フォールバックで生 JSON を出さないようにする。
// 地番をログ/Sentry に出さない（payload に地番を含めないため、エラーオブジェクトをそのまま送出してよい）。

/** ネットワーク断・タイムアウト（レスポンス未到達）時のメッセージ。 */
export const NETWORK_ERROR_MESSAGE =
  "通信環境を確認のうえ、時間をおいて再度お試しください。";

/** セッション切れ（トークンリフレッシュにも失敗した実 401）時のメッセージ。 */
export const SESSION_EXPIRED_MESSAGE =
  "セッションの有効期限が切れました。お手数ですが再度ログインしてください。";

/** handlePropertyCodeApiError の挙動を呼び出し側ごとに切り替えるオプション。 */
export interface HandlePropertyCodeApiErrorOptions {
  /** どの分岐にも該当しない場合に表示する汎用トースト文言（必須）。 */
  fallbackMessage: string;
  /** 409 Conflict 専用メッセージ。指定時のみ 409 を専用処理する（取得実行のみ使用）。 */
  conflictMessage?: string;
  /** 400 BadRequest のバリデーションエラー（nonFieldErrors/detail）を表示するか（既定 false）。 */
  handleBadRequest?: boolean;
  /**
   * 400 でフィールド単位のエラー（{ items: ["..."] } 等のキー別配列）も連結して
   * 表示するか（既定 false）。handleBadRequest とあわせて指定する（一括取得の依頼で使用）。
   */
  handleFieldErrors?: boolean;
}

/**
 * 登記取得画面の API エラーを分類してトースト表示・Sentry 送信する。
 * - 非 Axios 例外（コードのバグ等）: Sentry.captureException + 汎用トースト
 * - ネットワーク断・タイムアウト（レスポンス未到達）: Sentry 送信せず通信環境確認の文言
 * - 403: 閲覧専用ユーザー向けメッセージ
 * - 401: トークンリフレッシュ失敗＝実セッション切れ。再ログイン誘導の文言
 * - 409: conflictMessage 指定時のみ専用メッセージ
 * - 400: handleBadRequest 指定時に nonFieldErrors（string 要素のみ）/ detail を表示。空ボディ・想定外形状は汎用 + Sentry
 * - 5xx: Sentry.captureException + 汎用トースト
 * - その他: 汎用トースト
 * @param e catch した例外
 * @param options 呼び出し側ごとの分岐設定
 */
export const handlePropertyCodeApiError = (
  e: unknown,
  options: HandlePropertyCodeApiErrorOptions
): void => {
  const {
    fallbackMessage,
    conflictMessage,
    handleBadRequest = false,
    handleFieldErrors = false,
  } = options;

  // 非 Axios 例外（想定外の throw・コードのバグ）は握りつぶさず Sentry へ送る。
  if (!isAxiosError(e)) {
    Sentry.captureException(e);
    toast.error(fallbackMessage);
    return;
  }

  // ネットワーク断・タイムアウト（レスポンス未到達）はサーバ起因ではないため Sentry に送らず、
  // 通信環境の確認を促す。オフライン操作のたびに Sentry ノイズを出さない。
  if (e.response == null) {
    toast.error(NETWORK_ERROR_MESSAGE);
    return;
  }

  const status = e.response.status;

  // 403: 閲覧専用ユーザー
  if (status === HttpStatusCode.Forbidden) {
    toast.error(READ_ONLY_TOOLTIP_MESSAGE);
    return;
  }

  // 401: apiClient のインターセプタによる自動リフレッシュにも失敗した実セッション切れ。
  //      汎用文言（時間をおいて再試行）はミスリードのため、再ログインを促す。
  if (status === HttpStatusCode.Unauthorized) {
    toast.error(SESSION_EXPIRED_MESSAGE);
    return;
  }

  // 409: 同一物件で取得処理が進行中（履歴の整合性を壊さない）
  if (conflictMessage != null && status === HttpStatusCode.Conflict) {
    toast.error(conflictMessage);
    return;
  }

  // 400: フィールド単位/非フィールドのバリデーションエラー。
  if (handleBadRequest && status === HttpStatusCode.BadRequest) {
    // 空ボディは {} に正規化して観測パス（Sentry）へ確実に到達させる。
    const data = (e.response.data ?? {}) as {
      nonFieldErrors?: unknown;
      detail?: unknown;
    } & Record<string, unknown>;
    // nonFieldErrors は string 要素のみ採用し、非 string 要素の "[object Object]" 表示を防ぐ。
    const nonFieldMessages = Array.isArray(data.nonFieldErrors)
      ? data.nonFieldErrors.filter((m): m is string => typeof m === "string")
      : [];
    if (nonFieldMessages.length > 0) {
      toast.error(nonFieldMessages.join("\n"));
      return;
    }
    if (typeof data.detail === "string") {
      toast.error(data.detail);
      return;
    }
    // フィールド単位のエラー（キー別の string 配列）を1つに連結して表示する
    if (handleFieldErrors) {
      const fieldMessages = Object.values(data)
        .flat()
        .filter((m): m is string => typeof m === "string");
      if (fieldMessages.length > 0) {
        toast.error(fieldMessages.join("\n"));
        return;
      }
    }
    // 想定外の 400 形状（空ボディ含む）は生 JSON を画面に出さず、汎用メッセージ + Sentry で観測する。
    Sentry.captureException(e);
    toast.error(fallbackMessage);
    return;
  }

  // 分類から漏れた 4xx（handleBadRequest=false の 400/422 など）と 5xx を Sentry で観測する。
  // 403/401/409/handleBadRequest=true の 400 は上の各分岐で早期 return 済みのためここには到達せず、
  // 二重送信は発生しない。ネットワーク断も上で早期 return 済み。
  if (status >= HttpStatusCode.BadRequest) {
    Sentry.captureException(e);
  }
  toast.error(fallbackMessage);
};
